// pages/monitor/panels/ems/EmsStrategy.js
// 《云平台（能源管家）需求文档》2.7.6 策略管理（p28–32）
// 差异 #3：策略功率来源为「预置策略」或「智能策略」
// 差异 #10：「智能策略」为能源管家独有；不含削峰填谷的日前策略、光储充的计划曲线
import React, { useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import { useLang, t } from '../../consoles/ems/langContext';
import { Table, InputNumber, Input, Select, Button, Tag, Segmented, Checkbox, Switch, message,
} from 'antd';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ReferenceLine,
} from 'recharts';
import { getStation, getDevices, getPresetStrategy, getStrategyCurve, getSmartStrategy } from '../../../../config/stationData';

const tick = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const grid = 'rgba(255,255,255,0.08)';
const tooltipStyle = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const DEGREE = ['全站（总表）', '按并网点', '按时段'];
const DEGREE_EN = { '全站（总表）': 'Whole Station (Main Meter)', '按并网点': 'By Grid Point', '按时段': 'By Time Slot' };
const PROTECTION_EN = { '逆功率保护': 'Reverse Power Protection', '需量保护': 'Demand Protection' };

function KvRow({ label, children }) {
  return (
    <div className="dev-kv-row">
      <span className="dev-kv-k">{label}</span>
      <span className="dev-kv-v" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{children}</span>
    </div>
  );
}

// ---------- 预置策略 ----------
function Preset({ st }) {
  const { lang } = useLang();
  const dev = getDevices(st.id);
  const [mode, setMode] = useState('day');
  const [tpl, setTpl] = useState(() => getPresetStrategy().map((r) => ({ ...r, socHigh: 95, socLow: 12 })));
  const [strategies, setStrategies] = useState(['逆功率保护', '需量保护']);
  const [enabled, setEnabled] = useState(st.strategyOn);
  const [reverse, setReverse] = useState({ limit: 20, target: 25, degree: '全站（总表）', targets: ['PCS-01'] });
  const [demand, setDemand] = useState({ limit: 420, target: 415, degree: '全站（总表）' });
  const curve = getStrategyCurve();

  const upd = (key, field, val) => setTpl((arr) => arr.map((r) => (r.key === key ? { ...r, [field]: val } : r)));
  const del = (key) => setTpl((arr) => arr.filter((r) => r.key !== key));
  const addRow = () => setTpl((arr) => [...arr, { key: Date.now(), start: '00:00', end: '00:00', power: 0, state: '待机', rule: '自定义', pcs: '全部 PCS', socHigh: 95, socLow: 12 }]);

  return (
    <>
      <div className="panel">
        <div className="panel-title"><span>{t(lang, '策略运行', 'Strategy Operation')}</span><small>{t(lang, '配置当前站点策略应用情况', 'Configure strategy application for the current station')}</small></div>
        <KvRow label={t(lang, '主策略', 'Main Strategy')}><Tag color="blue">{t(lang, '削峰填谷', 'Peak Shaving & Valley Filling')}</Tag><span className="ems-note" style={{ margin: 0 }}>{t(lang, '一般情况下主策略为削峰填谷', 'Normally the main strategy is peak shaving & valley filling')}</span></KvRow>
        <KvRow label={t(lang, '其他策略', 'Other Strategies')}>
          <Checkbox.Group value={strategies} onChange={setStrategies} options={['逆功率保护', '需量保护'].map((o) => ({ value: o, label: t(lang, o, PROTECTION_EN[o]) }))} />
        </KvRow>
        <KvRow label={t(lang, '启用状态', 'Enabled Status')}>
          <Switch checked={enabled} onChange={setEnabled} />
          <span className="ems-note" style={{ margin: 0 }}>{enabled ? t(lang, '策略运行中', 'Strategy Running') : t(lang, '策略已停止', 'Strategy Stopped')}</span>
        </KvRow>
      </div>

      <div className="panel">
        <div className="panel-title"><span>{t(lang, '削峰填谷', 'Peak Shaving & Valley Filling')}</span><small>{t(lang, '策略配置 + 策略模板 + 策略模板曲线', 'Strategy config + strategy template + template curve')}</small></div>
        <div className="ems-inline">
          <span className="ems-inline-label">{t(lang, '策略配置', 'Strategy Config')}</span>
          <Segmented value={mode} onChange={setMode} options={[{ value: 'day', label: t(lang, '按日策略', 'Daily Strategy') }, { value: 'month', label: t(lang, '按月策略', 'Monthly Strategy') }]} />
          <span className="ems-note" style={{ margin: 0 }}>{t(lang, '可配置不同时间段的充放电功率与 SOC 保护；日策略优先于月策略', 'Configurable charge/discharge power & SOC protection per time slot; daily strategy takes precedence over monthly')}</span>
        </div>

        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '开始时间', 'Start Time'), dataIndex: 'start' },
            { title: t(lang, '结束时间', 'End Time'), dataIndex: 'end' },
            {
              title: t(lang, '充放电功率 (kW)', 'Charge/Discharge Power (kW)'), dataIndex: 'power', align: 'right',
              render: (v, r) => <InputNumber size="small" value={v} onChange={(x) => upd(r.key, 'power', x)} style={{ width: 110 }} />,
            },
            { title: t(lang, '状态', 'Status'), dataIndex: 'state', render: (v) => <Tag color={v === '充电' ? 'blue' : v === '放电' ? 'green' : 'default'}>{t(lang, v)}</Tag> },
            { title: t(lang, '分配规则', 'Allocation Rule'), dataIndex: 'rule', render: (v) => t(lang, v, { '自定义': 'Custom', '全部 PCS': 'All PCS' }[v] || v) },
            {
              title: t(lang, 'SOC 上限 (%)', 'SOC High (%)'), dataIndex: 'socHigh', align: 'right',
              render: (v, r) => <InputNumber size="small" min={0} max={100} value={v} onChange={(x) => upd(r.key, 'socHigh', x)} style={{ width: 80 }} />,
            },
            {
              title: t(lang, 'SOC 下限 (%)', 'SOC Low (%)'), dataIndex: 'socLow', align: 'right',
              render: (v, r) => <InputNumber size="small" min={0} max={100} value={v} onChange={(x) => upd(r.key, 'socLow', x)} style={{ width: 80 }} />,
            },
            { title: t(lang, '操作', 'Action'), render: (_, r) => <Button size="small" danger type="text" onClick={() => del(r.key)}>{t(lang, '删除', 'Delete')}</Button> },
          ]}
          dataSource={tpl}
        />
        <div className="ems-inline" style={{ marginTop: 10 }}>
          <Button size="small" onClick={addRow}>{t(lang, '新增时段', 'Add Time Slot')}</Button>
          <Button size="small" type="primary" onClick={() => message.success(t(lang, '削峰填谷策略模板已保存', 'Peak shaving & valley filling strategy template saved'))}>{t(lang, '保存模板', 'Save Template')}</Button>
        </div>

        <div className="panel-title" style={{ marginTop: 16 }}><span>{t(lang, '策略模板曲线', 'Strategy Template Curve')}</span><small>{t(lang, '预览策略模板的充放电功率', 'Preview charge/discharge power of the template')}</small></div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={curve} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="clock" tick={tick} interval={5} minTickGap={24} stroke={grid} />
            <YAxis tick={tick} stroke={grid} unit="kW" width={56} />
            <RTooltip contentStyle={tooltipStyle} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" />
            <Line type="stepAfter" dataKey="power" name={t(lang, '策略功率', 'Strategy Power')} stroke="#52e8b0" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid-2">
        <div className="panel">
          <div className="panel-title"><span>{t(lang, '逆功率保护', 'Reverse Power Protection')}</span><small>{t(lang, '保护总表功率不低于限制值，避免逆流', 'Keeps main-meter power from falling below the limit to avoid backflow')}</small></div>
          <KvRow label={t(lang, '限制值', 'Limit')}><InputNumber value={reverse.limit} onChange={(v) => setReverse((s) => ({ ...s, limit: v }))} addonAfter="kW" style={{ width: 160 }} /></KvRow>
          <KvRow label={t(lang, '目标值', 'Target')}><InputNumber value={reverse.target} onChange={(v) => setReverse((s) => ({ ...s, target: v }))} addonAfter="kW" style={{ width: 160 }} /><span className="ems-note" style={{ margin: 0 }}>{t(lang, '一般略高于限制值 1~5 kW', 'Usually 1~5 kW above the limit')}</span></KvRow>
          <KvRow label={t(lang, '保护程度', 'Protection Level')}><Select value={reverse.degree} onChange={(v) => setReverse((s) => ({ ...s, degree: v }))} options={DEGREE.map((d) => ({ value: d, label: t(lang, d, DEGREE_EN[d]) }))} style={{ width: 200 }} /></KvRow>
          <KvRow label={t(lang, '保护目标', 'Protection Targets')}>
            <Checkbox.Group
              value={reverse.targets}
              onChange={(v) => setReverse((s) => ({ ...s, targets: v }))}
              options={dev.pcs.map((p) => ({ value: p.name, label: p.name })).concat([{ value: '总表', label: t(lang, '总表', 'Main Meter') }])}
            />
          </KvRow>
          <div className="ems-note">{t(lang, '强电版本可勾选多个保护目标。', 'The strong-current version allows selecting multiple protection targets.')}</div>
        </div>

        <div className="panel">
          <div className="panel-title"><span>{t(lang, '需量控制', 'Demand Control')}</span><small>{t(lang, '保护总表功率不高于限制值，避免超需', 'Keeps main-meter power below the limit to avoid over-demand')}</small></div>
          <KvRow label={t(lang, '限制值', 'Limit')}><InputNumber value={demand.limit} onChange={(v) => setDemand((s) => ({ ...s, limit: v }))} addonAfter="kW" style={{ width: 160 }} /></KvRow>
          <KvRow label={t(lang, '目标值', 'Target')}><InputNumber value={demand.target} onChange={(v) => setDemand((s) => ({ ...s, target: v }))} addonAfter="kW" style={{ width: 160 }} /><span className="ems-note" style={{ margin: 0 }}>{t(lang, '一般略低于限制值 1~5 kW', 'Usually 1~5 kW below the limit')}</span></KvRow>
          <KvRow label={t(lang, '保护程度', 'Protection Level')}><Select value={demand.degree} onChange={(v) => setDemand((s) => ({ ...s, degree: v }))} options={DEGREE.map((d) => ({ value: d, label: t(lang, d, DEGREE_EN[d]) }))} style={{ width: 200 }} /></KvRow>
          <div className="ems-inline" style={{ marginTop: 14 }}>
            <Button type="primary" onClick={() => message.success(t(lang, '预置策略参数已保存', 'Preset strategy parameters saved'))}>{t(lang, '保存', 'Save')}</Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------- 智能策略（本类型独有） ----------
function Smart() {
  const { lang } = useLang();
  const base = getSmartStrategy();
  const [params, setParams] = useState(base.params);
  const [reverse, setReverse] = useState(base.reverse);
  const [demand, setDemand] = useState(base.demand);
  const upd = (k, v) => setParams((s) => ({ ...s, [k]: v }));
  return (
    <>
      <div className="panel">
        <div className="panel-title"><span>{t(lang, '智能策略 · 基本参数', 'Smart Strategy · Basic Parameters')}</span><small>{t(lang, '系统根据参数自动规划策略，保证经济性运行', 'The system plans the strategy automatically from the parameters to ensure economical operation')}</small></div>
        <div className="grid-2 grid-2-even">
          <div>
            <KvRow label={t(lang, '运行模式', 'Run Mode')}><Input value={params.mode} onChange={(e) => upd('mode', e.target.value)} style={{ width: 220 }} /></KvRow>
            <KvRow label={t(lang, '电价类型', 'Tariff Type')}><Select value={params.tariffType} onChange={(v) => upd('tariffType', v)} style={{ width: 220 }} options={[{ value: '分时电价（尖峰平谷）', label: t(lang, '分时电价（尖峰平谷）', 'TOU Tariff (Sharp-Peak-Flat-Valley)') }, { value: '固定电价', label: t(lang, '固定电价', 'Fixed Tariff') }]} /></KvRow>
            <KvRow label={t(lang, '合同类型', 'Contract Type')}><Select value={params.contract} onChange={(v) => upd('contract', v)} style={{ width: 220 }} options={[{ value: '最大需量合同', label: t(lang, '最大需量合同', 'Max Demand Contract') }, { value: '变压器容量合同', label: t(lang, '变压器容量合同', 'Transformer Capacity Contract') }]} /></KvRow>
          </div>
          <div>
            <KvRow label={t(lang, '变压器容量', 'Transformer Capacity')}><InputNumber value={params.transformerCapacity} onChange={(v) => upd('transformerCapacity', v)} addonAfter="kVA" style={{ width: 200 }} /></KvRow>
            <KvRow label={t(lang, '储能容量', 'Storage Capacity')}><InputNumber value={params.storageCapacity} onChange={(v) => upd('storageCapacity', v)} addonAfter="kWh" style={{ width: 200 }} /></KvRow>
            <KvRow label={t(lang, 'SOC 上限', 'SOC High')}><InputNumber value={params.socHigh} onChange={(v) => upd('socHigh', v)} addonAfter="%" style={{ width: 200 }} /></KvRow>
            <KvRow label={t(lang, 'SOC 下限', 'SOC Low')}><InputNumber value={params.socLow} onChange={(v) => upd('socLow', v)} addonAfter="%" style={{ width: 200 }} /></KvRow>
          </div>
        </div>
        <div className="ems-inline" style={{ marginTop: 14 }}>
          <Button type="primary" onClick={() => message.success(t(lang, '智能策略基本参数已保存，系统将自动规划策略', 'Smart strategy parameters saved; the system will plan the strategy automatically'))}>{t(lang, '保存并规划策略', 'Save & Plan Strategy')}</Button>
          <Tag color="purple">{t(lang, '能源管家独有', 'EMS Exclusive')}</Tag>
        </div>
      </div>

      <div className="grid-2">
        <div className="panel">
          <div className="panel-title"><span>{t(lang, '逆功率保护', 'Reverse Power Protection')}</span></div>
          <KvRow label={t(lang, '限制值', 'Limit')}><InputNumber value={reverse.limit} onChange={(v) => setReverse((s) => ({ ...s, limit: v }))} addonAfter="kW" style={{ width: 160 }} /></KvRow>
          <KvRow label={t(lang, '目标值', 'Target')}><InputNumber value={reverse.target} onChange={(v) => setReverse((s) => ({ ...s, target: v }))} addonAfter="kW" style={{ width: 160 }} /></KvRow>
          <KvRow label={t(lang, '保护程度', 'Protection Level')}><Select value={reverse.degree} onChange={(v) => setReverse((s) => ({ ...s, degree: v }))} options={DEGREE.map((d) => ({ value: d, label: t(lang, d, DEGREE_EN[d]) }))} style={{ width: 200 }} /></KvRow>
        </div>
        <div className="panel">
          <div className="panel-title"><span>{t(lang, '需量控制', 'Demand Control')}</span></div>
          <KvRow label={t(lang, '限制值', 'Limit')}><InputNumber value={demand.limit} onChange={(v) => setDemand((s) => ({ ...s, limit: v }))} addonAfter="kW" style={{ width: 160 }} /></KvRow>
          <KvRow label={t(lang, '目标值', 'Target')}><InputNumber value={demand.target} onChange={(v) => setDemand((s) => ({ ...s, target: v }))} addonAfter="kW" style={{ width: 160 }} /></KvRow>
          <KvRow label={t(lang, '保护程度', 'Protection Level')}><Select value={demand.degree} onChange={(v) => setDemand((s) => ({ ...s, degree: v }))} options={DEGREE.map((d) => ({ value: d, label: t(lang, d, DEGREE_EN[d]) }))} style={{ width: 200 }} /></KvRow>
        </div>
      </div>
    </>
  );
}

// ---------- 手动策略 ----------
function Manual({ st }) {
  const { lang } = useLang();
  const dev = getDevices(st.id);
  const [unit, setUnit] = useState(dev.pcs[0].name);
  const [mode, setMode] = useState('grid');
  const [offGrid, setOffGrid] = useState({ freq: 50, voltage: 400 });
  const [grid, setGrid] = useState({ power: 200, socHigh: 95, socLow: 12 });
  return (
    <div className="panel">
      <div className="panel-title"><span>{t(lang, '储能单元模式', 'Storage Unit Mode')}</span><small>{t(lang, '配置当前储能单元的工作状态，主要用于测试场景', 'Set the working state of the storage unit, mainly for testing')}</small></div>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '储能单元', 'Storage Unit')}</span>
        <Select value={unit} onChange={setUnit} style={{ width: 180 }} options={dev.pcs.map((p) => ({ value: p.name, label: p.name }))} />
        <span className="ems-inline-label">{t(lang, '工作状态', 'Working State')}</span>
        <Segmented value={mode} onChange={setMode} options={[{ value: 'offgrid', label: t(lang, '离网模式', 'Off-grid Mode') }, { value: 'grid', label: t(lang, '并网模式', 'Grid Mode') }]} />
      </div>
      {mode === 'offgrid' ? (
        <>
          <KvRow label={t(lang, '频率', 'Frequency')}><InputNumber value={offGrid.freq} onChange={(v) => setOffGrid((s) => ({ ...s, freq: v }))} addonAfter="Hz" style={{ width: 180 }} /></KvRow>
          <KvRow label={t(lang, '电压', 'Voltage')}><InputNumber value={offGrid.voltage} onChange={(v) => setOffGrid((s) => ({ ...s, voltage: v }))} addonAfter="V" style={{ width: 180 }} /></KvRow>
          <div className="ems-note">{t(lang, '离网模式：下发离网运行，并设定输出频率与电压。', 'Off-grid mode: dispatch off-grid operation and set the output frequency & voltage.')}</div>
        </>
      ) : (
        <>
          <KvRow label={t(lang, '功率', 'Power')}><InputNumber value={grid.power} onChange={(v) => setGrid((s) => ({ ...s, power: v }))} addonAfter="kW" style={{ width: 180 }} /></KvRow>
          <KvRow label={t(lang, 'SOC 上限', 'SOC High')}><InputNumber value={grid.socHigh} onChange={(v) => setGrid((s) => ({ ...s, socHigh: v }))} addonAfter="%" style={{ width: 180 }} /></KvRow>
          <KvRow label={t(lang, 'SOC 下限', 'SOC Low')}><InputNumber value={grid.socLow} onChange={(v) => setGrid((s) => ({ ...s, socLow: v }))} addonAfter="%" style={{ width: 180 }} /></KvRow>
          <div className="ems-note">{t(lang, '并网模式：下发并网功率，并设定 SOC 上下限值。', 'Grid mode: dispatch grid power and set the SOC high/low limits.')}</div>
        </>
      )}
      <div className="ems-inline" style={{ marginTop: 14 }}>
        <Button type="primary" onClick={() => message.success(t(lang, `${unit} 下发手动策略成功`, `${unit} manual strategy dispatched`))}>{t(lang, '下发', 'Dispatch')}</Button>
      </div>
    </div>
  );
}

export default function EmsStrategy({ station }) {
  const { lang } = useLang();
  const st = station || getStation('xz');
  const items = [
    { key: 'preset', label: t(lang, '预置策略', 'Preset Strategy'), children: <Preset st={st} /> },
    { key: 'smart', label: t(lang, '智能策略', 'Smart Strategy'), children: <Smart /> },
    { key: 'manual', label: t(lang, '手动策略', 'Manual Strategy'), children: <Manual st={st} /> },
  ];
  return (
    <>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '策略功率来源', 'Strategy Power Source')}</span>
        <Tag color="blue">{t(lang, '预置策略', 'Preset Strategy')}</Tag>
        <span className="ems-inline-label">{t(lang, '或', 'or')}</span>
        <Tag color="purple">{t(lang, '智能策略', 'Smart Strategy')}</Tag>
      </div>
      {/* 子页切换对齐光储充「控制中心」顶部胶囊 Tabs */}
      <ScrollTabs items={items} />
    </>
  );
}
